import React, {useEffect, useState} from 'react';
import Modal from '../../../../Layer/Modal/Modal';
import TextField, { Type } from '../../../../../Component/Input/TextField/TextField';
import Select from '../../../../../Component/Input/SelectInput/SelectInput';
import Radio from '../../../../../Component/Input/Radio/Radio';
import Checkbox from '../../../../../Component/Input/Checkbox/Checkbox';
import DatePicker from '../../../../../Component/Input/DatePicker/DatePicker';
import DateSelector from '../../../../../Component/Input/DateSelector/DateSelector';
import moment from 'moment';
import { ValidationError } from '../../../../../../shared/Common/Error/ValidationError';
import { useLocation } from 'react-router-dom';
import { CompanyFromApi } from '../../../../../../shared/Company/data/CompanyFromApi';
import { SelectorData } from 'application/Component/Input/DateSelector/data/SelectorData';
import { SelectValue } from '../../../../../../shared/Common/data/SelectValue';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

type BaseModal = {
    opened: boolean,
    modalTitle: string,
    text: string | null,
    name: string,
    companies: SelectValue[],
    date: Date | null,
    repeatAnnually: boolean,
    visible: boolean,
    type: string,
    movingType: SelectorData,
    okText: string,
    onCancel: () => any,
    companyValues: CompanyFromApi[],
    onOk: (
        name: string,
        companies: string[],
        type: string,
        movingType: SelectorData,
        date: Date | null,
        repeatAnnually: boolean,
        text: string | null,
        visible: boolean,
        validate: (errors: ValidationError[]) => void) => void
};

function BaseModal(props: BaseModal) {
    const companyOptions = props.companyValues.map(companyValue => {
        return {
            label: companyValue.name,
            value: companyValue.id
        } as SelectValue
    });

    const [type, setType] = useState<string>(props.type);
    const [movingType, setMovingType] = useState<SelectorData>(props.movingType);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const [year, setYear] = useState<string | null>(searchParams.get('year'));

    const [text, setText] = useState<string>(props.text ?? '');
    const [textError, setTextError] = useState<string | null>(null);

    const [name, setName] = useState<string>(props.name ?? '');
    const [nameError, setNameError] = useState<string | null>(null);

    const [date, setDate] = useState<Date | null>(props.date ? moment(props.date).toDate() : null);
    const [dateError, setDateError] = useState<string | null>(null);

    const [companies, setCompanies] = useState<SelectValue[]>(props.okText == 'Edit' ? props.companies : companyOptions);
    const [companiesError, setCompaniesError] = useState<string | null>(null);

    const [repeatAnnually, setRepeatAnnually] = useState<boolean>(props.repeatAnnually ?? false);

    const [visible, setVisible] = useState<boolean>(props.visible ?? false);

    function resetAll(): void {
        setText('');
        setName('');
        setDate(null);
        setDateError(null);
        setCompanies([]);
        setCompaniesError(null);
        setRepeatAnnually(true);
        setVisible(true);
        setTextError(null);
        setNameError(null);
    }

    useEffect(() => {
        if (props.modalTitle === 'Create Professional Holiday') {
            resetAll()
        }
        if (props.companies.length === 0 && props.okText != 'Edit') {
            setCompanies(companyOptions);
        }
    }, [props.opened]);

    useEffect(() => {
        if (year && type == 'moving') {
            const newDate = moment([parseInt(year), parseInt(movingType.month) - 1])
                .day(movingType.dayOfWeek)
                .add(parseInt(movingType.weekNumber) - 1, 'weeks')
                .toDate();
            if (newDate.getMonth() != moment().month(parseInt(movingType.month) - 1).toDate().getMonth()) {
                newDate.setDate(newDate.getDate() + 7);
            }
            setDate(newDate);
        }
    }, [year, movingType, type]);

    function validate(errors: ValidationError[]): void {
        errors.forEach((error: ValidationError) => {
            if (error.property === 'professionalHoliday.text') {
                setTextError(error.message);
            } else if (error.property === 'professionalHoliday.companies') {
                setCompaniesError(error.message);
            } else if (error.property === 'professionalHoliday.date') {
                setDateError(error.message);
            } else if (error.property === 'professionalHoliday.name') {
                setNameError(error.message);
            }
        });
    }

    function changeType(newType: string) {
        setType(newType);
    }

    return (
        <Modal
            opened={ props.opened }
            modalTitle={ props.modalTitle }
            cancelText='Cancel'
            okText={ props.okText }
            onCancel={() => {
                props.onCancel();
                resetAll();
            }}
            onOk={ () => props.onOk(name, companies.map(company => company.value), type, movingType, date, repeatAnnually, text, visible, validate) }
        >
            <TextField
                label='Name'
                placeholder="Please enter holiday's name"
                required={ true }
                value={ name }
                type={ Type.Textarea }
                horizontal={ true }
                error={ nameError }
                onChange={ newName => {
                    setName(newName);
                    setNameError(null);
                    setCompaniesError(null);
                }}
                maxLength={100}
            />
            <Select
                label='Companies'
                required={ true }
                isMulti={ true }
                value={ companies }
                options={ companyOptions }
                error={ companiesError }
                onChange={ (newCompanies: SelectValue[]) => {
                    setCompanies(newCompanies);
                    setCompaniesError(null);
                }}
                isSearchable={ false }
            />
            <Radio
                label='Type'
                required={ true }
                name={ 'type' + props.okText }
                value={ type }
                onChange={ newType => {
                    changeType(newType);
                    setRepeatAnnually(true);
                }}
                radios={[
                    { label: 'Fixed date', value: 'fixed' },
                    { label: 'Moving date', value: 'moving' }
                ]}
            />
            <div style={{ display: type === 'moving' ? 'block' : 'none' }}>
                <DateSelector
                    year={year ? parseInt(year) : null}
                    data={ movingType }
                    label=""
                    required={false}
                    horizontal={true}
                    onChange={(newMovingType) => {
                        setMovingType(newMovingType);
                        setCompaniesError(null);
                    }}
                />

            </div>
            <DatePicker
                label='Date'
                required={ true }
                horizontal={ true }
                date = { date }
                error = { dateError }
                onChange={ newDate => {
                    setDate(newDate);
                    setDateError(null);
                    setCompaniesError(null);
                }}
                maxDate={ year ? new Date((parseInt(year) + 1) + '-12-31') : null}
                minDate={ year ? new Date(year + '-01-01') : null }
                disabled={ type === 'moving' }
            />
            { type == 'fixed' ?
            <div className={'app-title-block'}>
                <svg id="app-title" className={'tooltip-help'} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.0834 14.854C10.3056 14.854 10.4931 14.7776 10.6459 14.6248C10.7987 14.4721 10.8751 14.2846 10.8751 14.0623C10.8751 13.8401 10.7987 13.6526 10.6459 13.4998C10.4931 13.3471 10.3056 13.2707 10.0834 13.2707C9.86119 13.2707 9.67369 13.3471 9.52091 13.4998C9.36814 13.6526 9.29175 13.8401 9.29175 14.0623C9.29175 14.2846 9.36814 14.4721 9.52091 14.6248C9.67369 14.7776 9.86119 14.854 10.0834 14.854ZM9.35425 11.8123H10.5834C10.5834 11.4512 10.6286 11.1214 10.7188 10.8228C10.8091 10.5241 11.0904 10.1804 11.5626 9.7915C11.9931 9.43039 12.2987 9.07623 12.4792 8.729C12.6598 8.38178 12.7501 7.99984 12.7501 7.58317C12.7501 6.84706 12.5105 6.25678 12.0313 5.81234C11.5522 5.36789 10.9167 5.14567 10.1251 5.14567C9.44452 5.14567 8.84383 5.31581 8.323 5.65609C7.80216 5.99636 7.42369 6.46511 7.18758 7.06234L8.29175 7.479C8.44452 7.09011 8.67369 6.78803 8.97925 6.57275C9.2848 6.35748 9.64591 6.24984 10.0626 6.24984C10.5348 6.24984 10.9167 6.37831 11.2084 6.63525C11.5001 6.8922 11.6459 7.22206 11.6459 7.62484C11.6459 7.93039 11.5556 8.21859 11.3751 8.48942C11.1945 8.76025 10.9306 9.0415 10.5834 9.33317C10.1667 9.69428 9.85772 10.0519 9.65633 10.4061C9.45494 10.7603 9.35425 11.229 9.35425 11.8123ZM10.0001 18.3332C8.86119 18.3332 7.7848 18.1144 6.77091 17.6769C5.75703 17.2394 4.87161 16.6422 4.11466 15.8853C3.35772 15.1283 2.7605 14.2429 2.323 13.229C1.8855 12.2151 1.66675 11.1387 1.66675 9.99984C1.66675 8.84706 1.8855 7.76373 2.323 6.74984C2.7605 5.73595 3.35772 4.854 4.11466 4.104C4.87161 3.354 5.75703 2.76025 6.77091 2.32275C7.7848 1.88525 8.86119 1.6665 10.0001 1.6665C11.1529 1.6665 12.2362 1.88525 13.2501 2.32275C14.264 2.76025 15.1459 3.354 15.8959 4.104C16.6459 4.854 17.2397 5.73595 17.6772 6.74984C18.1147 7.76373 18.3334 8.84706 18.3334 9.99984C18.3334 11.1387 18.1147 12.2151 17.6772 13.229C17.2397 14.2429 16.6459 15.1283 15.8959 15.8853C15.1459 16.6422 14.264 17.2394 13.2501 17.6769C12.2362 18.1144 11.1529 18.3332 10.0001 18.3332ZM10.0001 17.0832C11.9723 17.0832 13.6459 16.3922 15.0209 15.0103C16.3959 13.6283 17.0834 11.9582 17.0834 9.99984C17.0834 8.02761 16.3959 6.354 15.0209 4.979C13.6459 3.604 11.9723 2.9165 10.0001 2.9165C8.04175 2.9165 6.37161 3.604 4.98966 4.979C3.60772 6.354 2.91675 8.02761 2.91675 9.99984C2.91675 11.9582 3.60772 13.6283 4.98966 15.0103C6.37161 16.3922 8.04175 17.0832 10.0001 17.0832Z" fill="#6A7885"/>
                </svg>
                <ReactTooltip
                    anchorId="app-title"
                    place="bottom"
                    content={ repeatAnnually
                        ? "The entered year will not be taken into account, and the holiday will appear in the calendar in the specified year and every following year on the specified date until it is changed/canceled."
                        : "The holiday will be displayed in the calendar only on the specified date in the specified year. In the following year, it will be shown in the list as inactive." }
                    noArrow={true}
                />
            </div> : null
            }
            <Checkbox
                label={ null }
                required={ null }
                checked={ repeatAnnually }
                checkBoxLabel='Repeat annually'
                onChange={ newRepeatAnnually => {
                    setRepeatAnnually(newRepeatAnnually)
                    setCompaniesError(null);
                }}
                switch={ false }
                disabled={ type === 'moving' }
            />
            <TextField
                label='Text'
                placeholder='Please enter text'
                required={ false }
                value={ text }
                type={ Type.Textarea }
                horizontal={ true }
                error={ textError }
                onChange={ newText => {
                    setText(newText);
                    setTextError(null);
                    setCompaniesError(null);
                }}
                maxLength={150}
            />
            <Checkbox
                disabled={ false }
                label={ 'Visibility' }
                required={ null }
                checked={ visible }
                checkBoxLabel='Shown'
                onChange={ newVisible => {
                    setVisible(newVisible);
                    setCompaniesError(null);
                }}
                switch={ true }
            />
        </Modal>
    );
}

export default BaseModal;